import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Grid,
  Divider,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { PageHeader } from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { hrService, EmployeeListItem } from '../../services/hrService';
import { procurementService, Vendor } from '../../services/procurementService';
import { projectService, Project } from '../../services/projectService';
import { COMMON_CATEGORIES, PaidBy, PAID_BY_OPTIONS, PaymentMethod, toNum } from './ExpensePage';

// ---------------------------------------------------------------------------
// Reference-data shapes local to this page -- mirrors the pattern used by
// PurchaseRequisitionFormPage for the same /cost-centers endpoint.
// ---------------------------------------------------------------------------

interface CostCenter {
  id: number;
  code: string;
  name: string;
  isActive?: boolean;
}

interface ExpenseFromApi {
  id: number;
  expense_date: string;
  employee_id: number | string | null;
  category: string;
  description: string | null;
  amount: number | string;
  tax_amount: number | string;
  cost_center_id: number | string | null;
  project_id: number | string | null;
  department: string | null;
  payment_method: PaymentMethod | null;
  paid_by: PaidBy;
  vendor_id: string | null;
  gl_account_code: string | null;
  remarks: string | null;
  status: string;
}

const PAYMENT_METHODS: PaymentMethod[] = ['CASH', 'BANK', 'CARD', 'UPI', 'PETTY_CASH'];

const todayISO = () => new Date().toISOString().slice(0, 10);

const ExpenseFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loadingRefData, setLoadingRefData] = useState(true);
  const [loadingExpense, setLoadingExpense] = useState(isEdit);

  const [expenseDate, setExpenseDate] = useState(todayISO());
  const [employee, setEmployee] = useState<EmployeeListItem | null>(null);
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [taxAmount, setTaxAmount] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [department, setDepartment] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('BANK');
  const [paidBy, setPaidBy] = useState<PaidBy>('COMPANY');
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [glAccountCode, setGlAccountCode] = useState('');
  const [remarks, setRemarks] = useState('');
  const [status, setStatus] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Load reference data (employees, cost centers, projects, vendors) once.
  useEffect(() => {
    setLoadingRefData(true);
    Promise.all([
      hrService.getEmployees({ limit: 500 }).catch(() => ({
        employees: [] as EmployeeListItem[],
        pagination: { total: 0, page: 1, limit: 0, totalPages: 0 },
      })),
      // Cost centers / projects / vendors are optional context -- a tenant
      // without that module enabled must not block recording an expense.
      api.get<{ costCenters: CostCenter[] }>('/cost-centers').catch(() => ({ costCenters: [] as CostCenter[] })),
      projectService.getProjects().catch(() => [] as Project[]),
      procurementService.getVendors().catch(() => ({ vendors: [] as Vendor[], count: 0 })),
    ])
      .then(([employeesRes, costCentersRes, projectsRes, vendorsRes]) => {
        setEmployees(employeesRes.employees || []);
        setCostCenters((costCentersRes.costCenters || []).filter((c) => c.isActive !== false));
        setProjects(projectsRes || []);
        setVendors((vendorsRes.vendors || []).filter((v) => v.is_active !== false));
      })
      .finally(() => setLoadingRefData(false));
  }, []);

  // In edit mode, load the expense once reference data is available so the
  // Autocomplete values can be matched to the loaded employee/vendor options.
  useEffect(() => {
    if (!isEdit || !id || loadingRefData) return;
    setLoadingExpense(true);
    api
      .get<{ expense: ExpenseFromApi }>(`/expenses/${id}`)
      .then(({ expense }) => {
        setExpenseDate(expense.expense_date ? expense.expense_date.slice(0, 10) : todayISO());
        setEmployee(employees.find((e) => String(e.id) === String(expense.employee_id)) || null);
        setCategory(expense.category || '');
        setDescription(expense.description || '');
        setAmount(String(expense.amount ?? ''));
        setTaxAmount(String(expense.tax_amount ?? ''));
        setCostCenterId(expense.cost_center_id ? String(expense.cost_center_id) : '');
        setProjectId(expense.project_id ? String(expense.project_id) : '');
        setDepartment(expense.department || '');
        setPaymentMethod(expense.payment_method || 'BANK');
        setPaidBy(expense.paid_by || 'COMPANY');
        setVendor(expense.vendor_id ? vendors.find((v) => String(v.id) === String(expense.vendor_id)) || null : null);
        setGlAccountCode(expense.gl_account_code || '');
        setRemarks(expense.remarks || '');
        setStatus(expense.status);
      })
      .catch((err: any) => setError(err.message || 'Failed to load expense'))
      .finally(() => setLoadingExpense(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, loadingRefData]);

  const totalAmount = useMemo(() => toNum(amount) + toNum(taxAmount), [amount, taxAmount]);

  // Edit is only permitted while the expense is still a draft -- once
  // submitted it is on the approval workflow tracked by ExpenseDetailPage.
  const canEdit = !isEdit || status === 'DRAFT';

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!expenseDate) errs.push('Expense date is required.');
    if (!employee) errs.push('An employee is required.');
    if (!category.trim()) errs.push('A category is required.');
    if (toNum(amount) <= 0) errs.push('Amount must be greater than 0.');
    if (toNum(taxAmount) < 0) errs.push('Tax amount cannot be negative.');
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setValidationErrors(errs);
    if (errs.length > 0) return;

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        expenseDate,
        employeeId: employee?.id,
        category: category.trim(),
        description: description.trim() || undefined,
        amount: toNum(amount),
        taxAmount: toNum(taxAmount),
        costCenterId: costCenterId ? Number(costCenterId) : undefined,
        projectId: projectId ? Number(projectId) : undefined,
        department: department.trim() || undefined,
        paymentMethod,
        paidBy,
        vendorId: vendor?.id || undefined,
        glAccountCode: glAccountCode.trim() || undefined,
        remarks: remarks.trim() || undefined,
      };

      const response =
        isEdit && id
          ? await api.put<{ expense: { id: number } }>(`/expenses/${id}`, payload)
          : await api.post<{ expense: { id: number } }>('/expenses', payload);

      navigate(`/finance/expenses/${response.expense.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to save expense');
    } finally {
      setSubmitting(false);
    }
  };

  const loading = loadingRefData || loadingExpense;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(isEdit && id ? `/finance/expenses/${id}` : '/finance/expenses')}
        sx={{ mb: 1 }}
      >
        Back
      </Button>

      <PageHeader
        title={isEdit ? 'Edit Expense' : 'New Expense'}
        subtitle="Record what was spent, why, and how it will be settled -- this becomes the source document for the approval and payment workflow."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!canEdit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This expense is {status} and can no longer be edited.
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((e) => (
            <Box key={e}>{e}</Box>
          ))}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Expense Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Expense Date"
              value={expenseDate}
              onChange={(e) => setExpenseDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              size="small"
              options={employees}
              value={employee}
              onChange={(_, v) => setEmployee(v)}
              getOptionLabel={(o) => `${o.employee_code} — ${o.first_name}${o.last_name ? ` ${o.last_name}` : ''}`}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              disabled={!canEdit}
              renderInput={(params) => <TextField {...params} label="Employee" required />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              freeSolo
              size="small"
              options={COMMON_CATEGORIES}
              value={category}
              onChange={(_, v) => setCategory(v || '')}
              onInputChange={(_, v) => setCategory(v)}
              disabled={!canEdit}
              renderInput={(params) => <TextField {...params} label="Category" required />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Department"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!canEdit}
              placeholder="What was this expense for?"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              disabled={!canEdit}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Tax Amount"
              value={taxAmount}
              onChange={(e) => setTaxAmount(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {formatIndianNumber(totalAmount)}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Cost Center (optional)"
              value={costCenterId}
              onChange={(e) => setCostCenterId(e.target.value)}
              disabled={!canEdit}
            >
              <MenuItem value="">Not assigned</MenuItem>
              {costCenters.length === 0 ? (
                <MenuItem value="" disabled>
                  No cost centers configured
                </MenuItem>
              ) : (
                costCenters.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.code} — {c.name}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Project (optional)"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              disabled={!canEdit}
            >
              <MenuItem value="">Not assigned</MenuItem>
              {projects.length === 0 ? (
                <MenuItem value="" disabled>
                  No projects configured
                </MenuItem>
              ) : (
                projects.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>
                    {p.project_code} — {p.project_name}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Payment Method"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
              disabled={!canEdit}
            >
              {PAYMENT_METHODS.map((m) => (
                <MenuItem key={m} value={m}>
                  {m.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Paid By"
              value={paidBy}
              onChange={(e) => setPaidBy(e.target.value as PaidBy)}
              disabled={!canEdit}
            >
              {PAID_BY_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>
                  {p.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {paidBy === 'EMPLOYEE_REIMBURSEMENT'
                ? 'Posts a credit to Accounts Payable in the employee’s name until reimbursed.'
                : 'Posts a credit to the company’s bank or cash account directly.'}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              size="small"
              options={vendors}
              value={vendor}
              onChange={(_, v) => setVendor(v)}
              getOptionLabel={(o) => `${o.company_name} (${o.vendor_code})`}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              disabled={!canEdit}
              renderInput={(params) => <TextField {...params} label="Vendor (optional)" />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="GL Account Code (optional)"
              value={glAccountCode}
              onChange={(e) => setGlAccountCode(e.target.value)}
              disabled={!canEdit}
              placeholder="Override the default expense account"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
      </Paper>

      {canEdit && (
        <>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button onClick={() => navigate(isEdit && id ? `/finance/expenses/${id}` : '/finance/expenses')}>
              Cancel
            </Button>
            <Button variant="contained" startIcon={<Save />} onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Expense'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ExpenseFormPage;
